import { useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { ActionMenu } from "../../ui/ActionMenu";
import { Button } from "../../ui/Button";
import { EmptyStateCard } from "../../ui/EmptyStateCard";
import { Skeleton } from "../../ui/Skeleton";
import type { ListState } from "./useListState";

export type Column<T> = {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Tailwind width/flex classes for md+ (e.g. "w-28 shrink-0"). Default grows. */
  className?: string;
  /** Provide to make the column sortable. */
  sortValue?: (row: T) => string | number;
  /** Hide below md (kept in the row menu / detail instead). */
  desktopOnly?: boolean;
};

export type RowAction<T> = {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: (row: T) => void;
  /** Items are grouped by this number, lowest first (order by frequency). A divider separates groups. */
  group: number;
  danger?: boolean;
  hidden?: (row: T) => boolean;
  /** Also shown as a hover quick action on desktop. */
  quick?: boolean;
};

type Props<T> = {
  rows: T[];
  rowId: (row: T) => string;
  columns: Column<T>[];
  actions: RowAction<T>[];
  /** Primary action shown on the row (e.g. Review, Publish). Return null for none. */
  primary?: (row: T) => { label: string; onClick: () => void } | null;
  onOpen?: (row: T) => void;
  titleCell: (row: T) => ReactNode;
  loading?: boolean;
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  list: ListState;
  pageSize?: number;
  empty: { title: string; message: string; action?: { label: string; onClick: () => void } };
  /** Label for aria and the select-all control, e.g. "modules". */
  noun: string;
};

function sortRows<T>(rows: T[], columns: Column<T>[], list: ListState): T[] {
  const col = columns.find((c) => c.key === list.sortKey && c.sortValue);
  if (!col?.sortValue) return rows;
  const get = col.sortValue;
  const dir = list.sortDir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a);
    const y = get(b);
    if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * dir;
  });
}

function RowMenu<T>({ row, actions }: { row: T; actions: RowAction<T>[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const visible = actions.filter((a) => !a.hidden?.(row));
  const groups = [...new Set(visible.map((a) => a.group))].sort((a, b) => a - b);
  // Destructive items always sit at the bottom, after everything else.
  const normal = visible.filter((a) => !a.danger);
  const danger = visible.filter((a) => a.danger);
  if (visible.length === 0) return null;
  return (
    <>
      <button
        ref={ref}
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-11 w-11 items-center justify-center rounded-control text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-2 ring-ring md:h-8 md:w-8"
      >
        <MoreHorizontal size={16} />
      </button>
      <ActionMenu open={open} onClose={() => setOpen(false)} anchorRef={ref}>
        <div role="menu">
          {groups.map((g, gi) => {
            const items = normal.filter((a) => a.group === g);
            if (items.length === 0) return null;
            return (
              <div key={g} className={gi > 0 ? "border-t border-border" : ""}>
                {items.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      a.onSelect(row);
                    }}
                    className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-muted"
                  >
                    {a.icon}
                    {a.label}
                  </button>
                ))}
              </div>
            );
          })}
          {danger.length > 0 && (
            <div className="border-t border-border">
              {danger.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    a.onSelect(row);
                  }}
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm text-destructive hover:bg-destructive/10"
                >
                  {a.icon}
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </ActionMenu>
    </>
  );
}

export function AdminDataTable<T>({
  rows, rowId, columns, actions, primary, onOpen, titleCell, loading, selected, onSelectedChange,
  list, pageSize = 25, empty, noun,
}: Props<T>) {
  const sorted = sortRows(rows, columns, list);
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const page = Math.min(list.page, pages);
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);
  const pageIds = pageRows.map(rowId);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someSelected = pageIds.some((id) => selected.has(id));

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange(next);
  }
  function toggleAll() {
    const next = new Set(selected);
    if (allSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    onSelectedChange(next);
  }

  if (loading) {
    return (
      <div className="space-y-px overflow-hidden rounded-tile border border-border bg-card" aria-busy="true" aria-label={`Loading ${noun}`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="hidden h-4 w-24 md:block" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyStateCard
        title={list.activeCount > 0 ? `No ${noun} match` : empty.title}
        message={list.activeCount > 0 ? "Try removing a filter or clearing the search." : empty.message}
        action={list.activeCount > 0 ? { label: "Clear filters", onClick: list.clear } : empty.action}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-tile border border-border bg-card" role="table" aria-label={noun}>
      {/* Sticky header (desktop) */}
      <div
        role="row"
        className="sticky top-0 z-10 hidden items-center gap-3 border-b border-border bg-card px-3 py-2 text-xs font-semibold text-muted-foreground md:flex"
      >
        <input
          type="checkbox"
          aria-label={`Select all ${noun} on this page`}
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = !allSelected && someSelected;
          }}
          onChange={toggleAll}
          className="h-4 w-4 shrink-0 cursor-pointer"
        />
        <div className="w-8 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-[2]">Name</div>
        {columns.map((c) => (
          <div key={c.key} role="columnheader" className={c.className ?? "flex-1"}>
            {c.sortValue ? (
              <button
                type="button"
                onClick={() => list.setSort(c.key)}
                className="inline-flex items-center gap-1 hover:text-foreground focus-visible:ring-2 ring-ring"
              >
                {c.header}
                {list.sortKey === c.key && (list.sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
              </button>
            ) : (
              c.header
            )}
          </div>
        ))}
        <div className="w-44 shrink-0 text-right">Actions</div>
      </div>

      <div className="divide-y divide-border">
        {pageRows.map((row) => {
          const id = rowId(row);
          const isSel = selected.has(id);
          const prim = primary?.(row) ?? null;
          const quick = actions.filter((a) => a.quick && !a.hidden?.(row));
          return (
            <div
              key={id}
              role="row"
              tabIndex={0}
              aria-selected={isSel}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" && onOpen) onOpen(row);
                if (e.key === " ") {
                  e.preventDefault();
                  toggle(id);
                }
              }}
              className={`group flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 outline-none focus-visible:ring-2 ring-inset ring-ring md:flex-nowrap ${
                isSel ? "bg-accent-soft" : "hover:bg-muted/50"
              }`}
            >
              <input
                type="checkbox"
                aria-label="Select row"
                checked={isSel}
                onChange={() => toggle(id)}
                className="h-4 w-4 shrink-0 cursor-pointer"
              />
              <div className="min-w-0 flex-1 basis-40 md:flex-[2] md:basis-auto">
                <div className="flex items-center gap-3">{titleCell(row)}</div>
              </div>
              {columns.map((c) => (
                <div
                  key={c.key}
                  role="cell"
                  className={`${c.className ?? "flex-1"} ${c.desktopOnly ? "hidden md:block" : ""} text-sm`}
                >
                  {c.cell(row)}
                </div>
              ))}
              <div className="ml-auto flex w-full shrink-0 items-center justify-end gap-1 md:w-44">
                {quick.length > 0 && (
                  <span className="hidden items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 md:flex">
                    {quick.map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        aria-label={a.label}
                        title={a.label}
                        onClick={() => a.onSelect(row)}
                        className="flex h-8 w-8 items-center justify-center rounded-control text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-2 ring-ring"
                      >
                        {a.icon}
                      </button>
                    ))}
                  </span>
                )}
                {prim && (
                  <Button size="sm" variant="outline" onClick={prim.onClick}>
                    {prim.label}
                  </Button>
                )}
                <RowMenu row={row} actions={actions} />
              </div>
            </div>
          );
        })}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm text-muted-foreground">
          <span>
            {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => list.setPage(page - 1)}
              className="flex h-9 w-9 items-center justify-center rounded-control hover:bg-secondary disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <span>
              {page} / {pages}
            </span>
            <button
              type="button"
              aria-label="Next page"
              disabled={page >= pages}
              onClick={() => list.setPage(page + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-control hover:bg-secondary disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
